import { FC } from 'react';
import { z } from 'zod';
import { DrawerHeader } from '../drawer/DrawerHeader';
import { Button, Checkbox, FormControlLabel, Select, Stack } from '@mui/material';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { ControlledFormField } from '../form/ControlledFormField';
import { DateRangePicker } from '../form/DateRangePicker';
import { JourneyFilters, useJourneyFilters } from './useJourneyFilters';
import { useTypedForm } from '../../../shared/fe/hooks/useForm';
import { DrawerWrapper } from '../drawer/DrawerWrapper';
import { PersonasCheckList } from '../form/PersonasCheckList';
import { TagsCheckList } from '../form/TagsCheckList';
import { SatisfactionsCheckList } from '../form/SatisfactionsCheckList';
import { OwnersCheckList } from '../form/OwnersCheckList';
import { MenuItem } from '@mui/material';
import { renderValue } from './detail/detailView/widgets/duration/DurationEditDrawer';

const journeyFilterDrawerSchema = z.object({});

const createFormSchema = (intl: IntlShape) =>
	z.object({
		duration: z.enum(['-', 'one_time', 'time_limited', 'continuous', 'occurence']).default('-').optional(),
		date: z
			.tuple([
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
				z
					.string()
					.datetime({
						offset: true,
					})
					.nullable(),
			])
			.nullable(),
		warningsOnly: z.boolean(),
		satisfactions: z.array(z.string()),
		tags: z.array(z.string()),
		personas: z.array(z.string()),
		owners: z.array(z.string()),
	});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const dateRangeToTuple = (dateRange: JourneyFilters['date']): [string, string] | undefined => {
	if (dateRange == null) {
		return undefined;
	}

	return [dateRange.start, dateRange.end];
};

const dateRangeFromTuple = (dateTuple: [string | null, string | null] | null): JourneyFilters['date'] => {
	if (dateTuple?.[0] == null || dateTuple[1] == null) {
		return undefined;
	}

	return { start: dateTuple[0], end: dateTuple[1] };
};

const JourneyFilterDrawer: FC = () => {
	const [filters, setFilters] = useJourneyFilters();
	const intl = useIntl();

	const form = useTypedForm({
		schema: createFormSchema(intl),
		defaultValues: {
			duration: Array.isArray(dateRangeToTuple(filters.date)) ? 'time_limited' : filters.duration,
			date: dateRangeToTuple(filters.date) || null,
			warningsOnly: filters.warningsOnly === 'true',
			satisfactions: filters.satisfactions ?? [],
			tags: filters.tags ?? [],
			personas: filters.personas ?? [],
			owners: filters.owners ?? [],
		},
	});

	const values = form.watch();

	const onSubmit = (values: FormValues) => {
		setFilters({
			duration: values.duration,
			date: dateRangeFromTuple(values.date),
			warningsOnly: values.warningsOnly ? values.warningsOnly.toString() : undefined,
			satisfactions: values.satisfactions.length > 0 ? values.satisfactions : undefined,
			tags: values.tags.length > 0 ? values.tags : undefined,
			personas: values.personas.length > 0 ? values.personas : undefined,
			owners: values.owners.length > 0 ? values.owners : undefined,
		});
	};

	const onClearAll = () => {
		setFilters({});
		form.reset({
			duration: '-',
			date: null,
			warningsOnly: false,
			satisfactions: [],
			tags: [],
			personas: [],
			owners: [],
		});
	};

	return (
		<form noValidate onSubmit={form.handleSubmit(onSubmit)} style={{ height: '100%' }}>
			<DrawerWrapper height={'100%'}>
				<DrawerHeader title={<FormattedMessage id={'drawer.journey-filter.title'} />} />
				<Stack width={'100%'} gap={1.5} height={'100%'} justifyContent={'space-between'}>
					<ControlledFormField
						form={form}
						name={'duration'}
						label={intl.formatMessage({ id: 'form.journey-filter.date.label.duration' })}
						render={({ id, name, value, onChange }) => (
							<Select id={id} name={name} value={value} renderValue={(value) => renderValue[value]} onChange={onChange}>
								{Object.entries(renderValue).map(([key, value]) => (
									<MenuItem key={key} value={key}>
										{value}
									</MenuItem>
								))}
							</Select>
						)}
					/>
					{values.duration === 'time_limited' && (
						<ControlledFormField
							form={form}
							name={'date'}
							label={intl.formatMessage({ id: 'form.journey-filter.date.label.date' })}
							render={({ id, name, value, onChange }) => <DateRangePicker id={id} name={name} value={value} onChange={onChange} />}
						/>
					)}
					<ControlledFormField
						form={form}
						name={'warningsOnly'}
						label={intl.formatMessage({ id: 'form.journey-filter.date.label.warnings' })}
						render={({ id, name, value, onChange }) => (
							<FormControlLabel
								label={intl.formatMessage({ id: 'form.journey-filter.date.label.warningsOnly' })}
								control={<Checkbox sx={{ marginLeft: -1 }} id={id} name={name} value={value} onChange={onChange} checked={value} />}
							/>
						)}
					/>
					<ControlledFormField
						form={form}
						name={'satisfactions'}
						label={intl.formatMessage({ id: 'form.journey-filter.date.label.satisfactions' })}
						render={({ value, onChange }) => <SatisfactionsCheckList value={value} onChange={onChange} />}
					/>
					<ControlledFormField
						form={form}
						name={'tags'}
						label={intl.formatMessage({ id: 'form.journey-filter.date.label.tags' })}
						render={({ value, onChange }) => <TagsCheckList value={value} onChange={onChange} size={'medium'} />}
					/>
					<ControlledFormField
						form={form}
						name={'personas'}
						label={intl.formatMessage({ id: 'form.journey-filter.date.label.personas' })}
						render={({ value, onChange }) => <PersonasCheckList value={value} onChange={onChange} size={'medium'} />}
					/>
					<ControlledFormField
						form={form}
						name={'owners'}
						label={intl.formatMessage({ id: 'form.journey-filter.date.label.owner' })}
						render={({ value, onChange }) => <OwnersCheckList value={value} onChange={onChange} size={'medium'} />}
					/>
					<Stack flexGrow={1} direction={'row'} justifyContent={'end'} alignItems={'end'} gap={3}>
						<Button variant={'text'} onClick={onClearAll} color={'secondary'} sx={{ flexGrow: 1 }}>
							<FormattedMessage id={'form.journey-filter.clear-all'} />
						</Button>
						<Button variant={'contained'} type={'submit'} color={'primary'} sx={{ flexGrow: 1 }}>
							<FormattedMessage id={'form.journey-filter.apply'} />
						</Button>
					</Stack>
				</Stack>
			</DrawerWrapper>
		</form>
	);
};

export { journeyFilterDrawerSchema, JourneyFilterDrawer };
